import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle, ChevronRight, ArrowUp } from 'lucide-react';
import { api } from '@/lib/api';
import { trackGarageAddClick } from '@/lib/analytics';

export function TrendingDiscussions() {
  const { data, isLoading } = useQuery({
    queryKey: ['garages', 'trending-discussions'],
    queryFn: () => api.garages.trendingDiscussions({ limit: 5, days: 7 }),
    staleTime: 60 * 60 * 1000,
  });

  if (!isLoading && (!data?.items || data.items.length === 0)) return null;

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-rose-500/5 to-transparent">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500/10">
          <MessageCircle className="h-4 w-4 text-rose-500" />
        </div>
        <h3 className="font-semibold text-text-primary">Trending Discussions</h3>
      </div>

      <div className="p-2">
        {isLoading ? (
          <div className="space-y-3 p-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-3 w-24 rounded bg-bg-tertiary" />
                <div className="mt-1.5 h-4 w-full rounded bg-bg-tertiary" />
              </div>
            ))}
          </div>
        ) : (
          data?.items.map((d) => (
            <Link
              key={d.id}
              to={d.post ? `/post/${d.post.id}` : `/garage/${d.garage.slug}`}
              onClick={() => trackGarageAddClick(d.garage.id, 'trending_discussions')}
              className="flex gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-bg-tertiary group"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
                  <span>{d.garage.emoji || '🏠'}</span>
                  <Link
                    to={`/garage/${d.garage.slug}`}
                    className="truncate font-medium hover:text-rose-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {d.garage.name}
                  </Link>
                  <span>·</span>
                  <span className="truncate">{d.user.displayName || d.user.username}</span>
                </div>
                <p className="mt-0.5 text-sm font-medium text-text-secondary group-hover:text-primary-400 transition-colors line-clamp-2">
                  {d.post?.title || d.title || 'Shared a post'}
                </p>
                <div className="mt-1 flex items-center gap-3 text-xs text-text-tertiary">
                  {d.upvoteCount > 0 && (
                    <span className="flex items-center gap-0.5">
                      <ArrowUp className="h-3 w-3" />
                      {d.upvoteCount}
                    </span>
                  )}
                  {d.commentCount > 0 && (
                    <span className="flex items-center gap-0.5">
                      <MessageCircle className="h-3 w-3" />
                      {d.commentCount}
                    </span>
                  )}
                  {d.post?.source?.name && (
                    <span>via {d.post.source.name}</span>
                  )}
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

      <Link
        to="/explore?tab=garages"
        className="flex items-center justify-center gap-1 border-t border-border-primary py-3 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
      >
        Join the conversation
        <ChevronRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
